import React, { CSSProperties, useEffect, useRef } from 'react'
import {
  AgoraVideoPlayer,
  ILocalVideoTrack,
  IRemoteVideoTrack,
  VideoPlayerConfig
} from 'agora-rtc-react'
import useElementSize from './Utils/useElementSize'
import useVideoDimensions from './Utils/useVideoDimensions'
import { fitContain, Rect, Size } from './Utils/videoLayout'

/** Geometry of a FittedVideo, reported to the parent layout. */
export interface VideoLayout {
  /** Size of the area available to the video. */
  box: Size
  /** Where the picture is drawn inside `box`; null until both sizes are known. */
  frame: Rect | null
  /** The stream's own dimensions (after rotation); null until the first frame. */
  source: Size | null
}

interface FittedVideoProps {
  videoTrack: ILocalVideoTrack | IRemoteVideoTrack
  fit?: VideoPlayerConfig['fit']
  /** Called when the geometry changes, and with null on unmount. */
  onLayout?: (layout: VideoLayout | null) => void
}

/**
 * Plays a video track at the right shape.
 *
 * With `fit: 'contain'` the player element is sized to the stream's own aspect ratio and centred
 * in the available area, so a portrait phone stream gets a portrait frame and a landscape one a
 * landscape frame. The SDK still renders with its own `contain` mode (never stretched or
 * cropped); the space around the frame shows the parent's background instead of the SDK's black
 * letterbox, and the parent can place other content there (see the adaptive pinned layout).
 * Other fit modes fill the area exactly as before.
 *
 * Resizing never interrupts playback: the element tree stays the same and AgoraVideoPlayer only
 * re-plays when the track itself changes. Only our wrapper is sized; the SDK's own elements are
 * left untouched.
 */
const FittedVideo = ({
  videoTrack,
  fit = 'cover',
  onLayout
}: FittedVideoProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<HTMLDivElement>(null)
  const box = useElementSize(boxRef)
  const source = useVideoDimensions(playerRef)
  const frame =
    fit === 'contain' && box && source ? fitContain(source, box) : null

  // Latest callback without re-running the effects below when the parent re-renders.
  const onLayoutRef = useRef(onLayout)
  onLayoutRef.current = onLayout

  useEffect(() => {
    if (box && onLayoutRef.current) onLayoutRef.current({ box, frame, source })
  }, [
    box?.width,
    box?.height,
    frame?.x,
    frame?.y,
    frame?.width,
    frame?.height,
    source?.width,
    source?.height
  ])

  useEffect(
    () => () => {
      if (onLayoutRef.current) onLayoutRef.current(null)
    },
    []
  )

  return (
    <div ref={boxRef} style={styles.box}>
      <div
        ref={playerRef}
        style={
          frame
            ? {
                ...styles.frame,
                left: frame.x,
                top: frame.y,
                width: frame.width,
                height: frame.height
              }
            : styles.fill
        }
      >
        <AgoraVideoPlayer
          style={styles.player}
          config={{ fit }}
          videoTrack={videoTrack}
        />
      </div>
    </div>
  )
}

const styles = {
  box: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden'
  } as CSSProperties,
  frame: {
    position: 'absolute',
    overflow: 'hidden'
  } as CSSProperties,
  fill: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%'
  } as CSSProperties,
  player: {
    width: '100%',
    height: '100%'
  } as CSSProperties
}

export default FittedVideo
